import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, LogIn } from 'lucide-react'

export default function Welcome() {
  useEffect(() => {
    document.title = 'HomiCareplus | Professional Healthcare at Your Doorstep'
  }, [])

  return (
    <div className="auth-theme min-h-[100dvh] lg:h-[100dvh] bg-[#fcfff8] relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top,_rgba(4,112,79,0.10),_transparent_60%)]" />

      <div className="relative mx-auto flex min-h-[100dvh] lg:h-[100dvh] w-full max-w-6xl items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-5">
        <div className="w-full">
          <div className="mb-6 lg:mb-5 flex items-center justify-start">
            <img
              src="/logos.png"
              alt="HomiCareplus"
              className="h-8 sm:h-8 w-auto max-w-[220px] object-contain"
            />
          </div>

          <div className="space-y-6 lg:space-y-8">
            <section className="max-w-2xl">
              <h1 className="mt-4 lg:mt-5 text-3xl font-bold tracking-[-0.04em] text-foreground sm:text-4xl lg:text-4xl xl:text-5xl">
                Choose how you want to
                <span className="block text-primary">continue.</span>
              </h1>
            </section>

            <section className="grid gap-4 lg:grid-cols-2 lg:gap-5">
              <div className="rounded border border-border bg-white/30 p-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:p-6">
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">For Patients</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-foreground">Find Care Quickly</h2>
                    <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                      Book and manage your healthcare appointments.
                    </p>
                  </div>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/8 text-primary">
                    <img
                      src="/patient.png"
                      alt=""
                      className="h-[22px] w-[22px] object-contain"
                      style={{ filter: 'brightness(0) saturate(100%) invert(35%) sepia(72%) saturate(1020%) hue-rotate(128deg) brightness(88%) contrast(96%)' }}
                    />
                  </div>
                </div>

                <div className="space-y-2.5">
                  <Link
                    to="/auth/login?role=patient"
                    className="flex items-center justify-center gap-2 rounded border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                  >
                    <LogIn size={16} />
                    Patient Login
                  </Link>
                  <Link
                    to="/auth/signup"
                    className="group flex items-center justify-center gap-2 rounded bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/92"
                  >
                    Create Patient Account
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>

              <div className="rounded border border-border bg-white/30 p-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:p-6">
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">For Providers</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-foreground">Offer Care</h2>
                    <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                      Offer services and manage your availability.
                    </p>
                  </div>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/8 text-primary">
                    <img
                      src="/provider.png"
                      alt=""
                      className="h-[25px] w-[25px] object-contain"
                      style={{ filter: 'brightness(0) saturate(100%) invert(35%) sepia(72%) saturate(1020%) hue-rotate(128deg) brightness(88%) contrast(96%)' }}
                    />
                  </div>
                </div>

                <div className="space-y-2.5">
                  <Link
                    to="/auth/login?role=provider"
                    className="flex items-center justify-center gap-2 rounded border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                  >
                    <LogIn size={16} />
                    Provider Login
                  </Link>
                  <Link
                    to="/auth/provider-signup"
                    className="group flex items-center justify-center gap-2 rounded bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/92"
                  >
                    Create Provider Account
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-6 lg:mt-5 border-t border-border pt-4 text-center md:text-left text-sm text-muted-foreground">
            By using our service you agree to our{' '}
            <Link to="/terms" className="text-foreground underline underline-offset-4 cursor-pointer">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-foreground underline underline-offset-4 cursor-pointer">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
